"use server";

import { cookies } from "next/headers";
import { redirect } from "next/navigation";
import { requestIp } from "@/lib/auth/current";
import { checkPassword, checkSecondFactor, LOCKED_MESSAGE, recordLogin } from "@/lib/auth/login";
import { createSession, revokeToken, WEB_TTL_MS } from "@/lib/auth/sessions";
import type { UserRow } from "@/lib/auth/users";
import { CHALLENGE_COOKIE, cookieOptions, SESSION_COOKIE, STAFF_COOKIE } from "@/lib/session";

export type LoginState =
  | { error?: string; field?: "email" | "password" | "code"; email?: string; twoFactor?: boolean }
  | undefined;

const WRONG =
  "El correo o la contraseña no coinciden. Revisa los datos; si no recuerdas tu contraseña, tu asesor te da una nueva.";

/** Solo acepta rutas internas para evitar redirecciones abiertas. */
function safeNext(value: FormDataEntryValue | null): string {
  const next = typeof value === "string" ? value : "";
  return next.startsWith("/") && !next.startsWith("//") && !next.startsWith("/login") ? next : "/";
}

/** Abre la sesión web (cookie httpOnly con token opaco) y lleva a cada quien a su lugar */
async function open(user: UserRow, nextValue: FormDataEntryValue | null): Promise<never> {
  const ip = await requestIp();
  const { token } = await createSession(user.id, "web", { ip });
  await recordLogin(user, "web", ip);
  const jar = await cookies();
  jar.delete(CHALLENGE_COOKIE);
  const staff = user.kind === "equipo";
  jar.delete(staff ? SESSION_COOKIE : STAFF_COOKIE);
  jar.set(staff ? STAFF_COOKIE : SESSION_COOKIE, token, cookieOptions(WEB_TTL_MS / 1000));
  const next = safeNext(nextValue);
  if (!staff) redirect(next);
  // La aseguradora aliada entra directo a sus entregas
  if (user.roles.length === 1 && user.roles[0] === "aseguradora") redirect("/consola/entregas");
  redirect(next.startsWith("/consola") ? next : "/consola");
}

export async function login(_prev: LoginState, formData: FormData): Promise<LoginState> {
  const email = String(formData.get("email") ?? "")
    .trim()
    .toLowerCase();
  const password = String(formData.get("password") ?? "");
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return {
      error: "Escribe un correo válido, por ejemplo nombre@empresa.com.",
      field: "email",
      email,
    };
  }
  if (!password)
    return { error: "Escribe tu contraseña para continuar.", field: "password", email };

  const result = await checkPassword(email, password, await requestIp());
  if (result.ok === false)
    return { error: result.reason === "locked" ? LOCKED_MESSAGE : WRONG, field: "password", email };
  if (result.ok === "2fa") {
    (await cookies()).set(CHALLENGE_COOKIE, result.challenge, cookieOptions(5 * 60));
    return { twoFactor: true, email };
  }
  return open(result.user, formData.get("next"));
}

/** Segundo paso del ingreso: código de la app autenticadora */
export async function verifyLoginCode(_prev: LoginState, formData: FormData): Promise<LoginState> {
  const challenge = (await cookies()).get(CHALLENGE_COOKIE)?.value;
  const code = String(formData.get("code") ?? "").replace(/\s/g, "");
  if (!challenge) return { error: "El tiempo para escribir el código terminó. Ingresa de nuevo." };
  if (!/^\d{6}$/.test(code))
    return {
      twoFactor: true,
      field: "code",
      error: "Escribe los 6 dígitos que muestra tu aplicación.",
    };
  const user = await checkSecondFactor(challenge, code, await requestIp());
  if (!user)
    return {
      twoFactor: true,
      field: "code",
      error: "El código no es correcto o ya venció. Escribe el que muestra ahora tu aplicación.",
    };
  return open(user, formData.get("next"));
}

async function closeSessions() {
  const jar = await cookies();
  await revokeToken(jar.get(SESSION_COOKIE)?.value);
  await revokeToken(jar.get(STAFF_COOKIE)?.value);
  jar.delete(SESSION_COOKIE);
  jar.delete(STAFF_COOKIE);
}

export async function logout(): Promise<void> {
  await closeSessions();
  redirect("/login");
}

/** Cierre automático tras 30 minutos sin actividad (lo dispara el panel desde el navegador) */
export async function logoutIdle(): Promise<void> {
  await closeSessions();
  redirect("/login?motivo=inactividad");
}
